// flow/components/FlowContextMenus.tsx
import ContextMenu from '@/components/menus/ContextMenu'
import PaneContextMenu from '@/components/menus/PaneContextMenu'
import SectionContextMenu from '@/components/menus/SectionContextMenu'
import type { Node } from '@xyflow/react'

interface FlowContextMenusProps {
  // Node context menu
  nodeMenu: any
  setNodeMenu: (menu: any) => void
  duplicateNode: (id: string) => void
  removeNode: (id: string) => void
  nodes: Node[]
  
  // Pane context menu
  paneMenu: any
  setPaneMenu: (menu: any) => void
  createAndAddNode: (nodeType: string) => void
  
  // Section context menu
  sectionMenu: any
  setSectionMenu: (menu: any) => void
  handleSectionStateChange: (nodeId: string, sectionId: string, state: any) => void
  updateNodeData: (nodeId: string, data: any) => void
  getNode: (id: string) => Node | undefined
  createDocumentNodeWithContent: (content: string, title: string, position: any) => void
}

export function FlowContextMenus({
  nodeMenu,
  setNodeMenu,
  duplicateNode,
  removeNode,
  nodes,
  paneMenu,
  setPaneMenu,
  createAndAddNode,
  sectionMenu,
  setSectionMenu,
  handleSectionStateChange,
  updateNodeData,
  getNode,
  createDocumentNodeWithContent,
}: FlowContextMenusProps) {
  // Section menu state logging removed - issue fixed

  return (
    <>
      {nodeMenu && (
        <ContextMenu
          {...nodeMenu}
          onDuplicate={(id) => {
            duplicateNode(id)
            setNodeMenu(null)
          }}
          onDelete={(id) => {
            // Check if this is a group node and delete children first
            if (nodeMenu?.nodeType === 'groupnode') {
              // Find all child nodes that have this node as parent
              const childNodes = nodes.filter(node => node.parentId === id)
              console.log('Context menu deleting group node:', id, 'with', childNodes.length, 'children:', childNodes.map(n => n.id))
              
              // Delete all child nodes first
              childNodes.forEach(childNode => {
                removeNode(childNode.id)
              })
            }
            
            // Delete the node itself
            removeNode(id)
            setNodeMenu(null)
          }}
          onClose={() => setNodeMenu(null)}
        />
      )}

      {paneMenu && paneMenu.isVisible && (
        <PaneContextMenu
          {...paneMenu}
          isVisible={paneMenu.isVisible}
          onCreateNode={createAndAddNode}
          onClose={() => setPaneMenu(null)}
        />
      )}

      {sectionMenu.isVisible && (
        <SectionContextMenu
          {...sectionMenu}
          isVisible={sectionMenu.isVisible}
          onStateChange={(state) => {
            if (sectionMenu.nodeId && sectionMenu.sectionId) {
              handleSectionStateChange(
                sectionMenu.nodeId,
                sectionMenu.sectionId,
                state
              )
              setSectionMenu({ isVisible: false })
            }
          }}
          onProtectChange={(isProtected) => {
            if (sectionMenu.nodeId && sectionMenu.sectionId) {
              // Update the node data
              updateNodeData(sectionMenu.nodeId, (prevData: any) => {
                const protectedSections = {
                  ...(prevData.protectedSections || {}),
                  [sectionMenu.sectionId]: isProtected,
                  [sectionMenu.sectionName]: isProtected,
                }

                // Dispatch custom event for node components to react to
                const event = new CustomEvent(
                  'section-protection-change',
                  {
                    detail: {
                      nodeId: sectionMenu.nodeId,
                      sectionId: sectionMenu.sectionId,
                      sectionName: sectionMenu.sectionName,
                      isProtected,
                    },
                  }
                )
                document.dispatchEvent(event)

                return { ...prevData, protectedSections }
              })

              setSectionMenu({ isVisible: false })
            }
          }}
          isProtected={
            sectionMenu.nodeId && sectionMenu.sectionId
              ? getNode(sectionMenu.nodeId)?.data?.protectedSections?.[
                  sectionMenu.sectionId
                ] ||
                getNode(sectionMenu.nodeId)?.data?.protectedSections?.[
                  sectionMenu.sectionName
                ] ||
                false
              : false
          }
          onClose={() => setSectionMenu({ isVisible: false })}
          onCreateDocument={(sectionContent: string) => {
            console.log(sectionContent)
            // Get source node to extract content and determine position
            const sourceNode = sectionMenu.nodeId
              ? getNode(sectionMenu.nodeId)
              : null
            const sourcePosition = sourceNode?.position || {
              x: window.innerWidth / 2,
              y: window.innerHeight / 2,
            }            // Extract actual section content from the source node
            let content = ''
            if (sourceNode?.data?.selectedSection && 
                typeof sourceNode.data.selectedSection === 'object' &&
                'content' in sourceNode.data.selectedSection) {
              const selectedSection = sourceNode.data.selectedSection as any
              content = `::: section ${selectedSection?.title || 'Untitled'}\n\n${selectedSection.content || 'Untitled Section'}\n:::`
            }
            console.log('content', content)

            // Create document node with the content
            createDocumentNodeWithContent(
              content,
              `Section: ${(sourceNode?.data?.selectedSection as any)?.title || 'Untitled'} from ${sourceNode?.data?.label || 'Unknown'}`,
              sourcePosition
            )

            setSectionMenu({ isVisible: false })
          }}
        />
      )}
    </>
  )
}

export default FlowContextMenus
